import { copy as sharedUiCopy } from '@shared/copy';
import { Skeleton, SkeletonFields, SkeletonMetaRows } from '@shared/ui/Skeleton';

/**
 * The pending state of `/pages/contact`.
 *
 * Four `PageSection`s and the action row that closes the form, in the order the
 * loaded surface draws them: the page's identity facts, the lede field, the
 * channel grid with its handle repeater, and the routing notice.
 *
 * The identity rows are drawn WITHOUT a panel, because `PageIdentity` renders a
 * bare `MetaGrid`: a bordered card here would put a box on the surface and then
 * take it away.
 *
 * The routing notice IS reserved, and it is the one block on this surface that
 * is: it is static copy this client owns, so it arrives with the form rather
 * than with the record, and a placeholder that skipped it would drop an alert
 * band into the page at the moment the payload lands.
 */
function SectionHeadSkeleton() {
  return (
    <div className="mb-4">
      <Skeleton className="h-7 w-[10rem]" />
    </div>
  );
}

export function ContactSkeleton() {
  return (
    <>
      <p role="status" className="sr-only">
        {sharedUiCopy.state.loading}
      </p>

      <section aria-hidden="true">
        <SectionHeadSkeleton />
        <SkeletonMetaRows rows={3} />
      </section>

      <section aria-hidden="true" className="mt-section-gap">
        <SectionHeadSkeleton />

        <div className="mb-4">
          <Skeleton className="mb-2 h-4 w-[7rem]" />
          <Skeleton className="h-[5rem] w-full rounded-sm" />
          <Skeleton className="mt-2 h-3 w-1/2" />
        </div>
      </section>

      <section aria-hidden="true" className="mt-section-gap">
        <SectionHeadSkeleton />
        <Skeleton className="mb-4 h-3 w-2/3 max-w-measure" />
        <SkeletonFields rows={3} />
      </section>

      <section aria-hidden="true" className="mt-section-gap">
        <SectionHeadSkeleton />
        <Skeleton className="h-[5rem] w-full rounded-md" />
      </section>

      <div
        aria-hidden="true"
        className="mt-4 flex flex-wrap gap-3 lg:mt-8 lg:border-t lg:border-border lg:pt-5"
      >
        <Skeleton className="h-[2.75rem] w-[7rem] rounded-sm" />
        <Skeleton className="h-[2.75rem] w-[6rem] rounded-sm" />
      </div>
    </>
  );
}
